import { action, computed, makeObservable } from 'mobx';
import { MatchType as MarketType, ItemType, getMarkets } from './markets';
import { europeanEliteTemplate, scotlandRegex, walesTemplateId } from '../EventsTemplates';
import { resolveCompetitionFlagCode } from '../resolveCompetitionFlagCode';
import { EventsCollectionList } from 'src/domains/sportsbook/state/eventsCollection/EventsCollectionList';
import { EventsCollectionState } from 'src/domains/sportsbook/state/eventsCollection/EventsCollectionState';
import {
    EventsCollectionQueryCompetitionType,
    EventsCollectionQueryModel,
} from 'src_common/common/websocket2/models/EventsCollectionQueryModel';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import { CompetitionId, EventId } from 'src_common/common/websocket2/id/WebsocketId';
import { Common } from 'src/domains/common/Common';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { UnifiedEventsListSelectTabState } from './UnifiedEventsListSelectTabState';
import { SportsTimeFilterType } from 'src/domains/sportsbook/webview/components/sportEventsListNew/SportsFilters/SportsTimeFilter';
import { DateTime } from 'src_common/utils/time/time';
import { matchesMarketFilterTemplate } from './matchesMarketFilterTemplate';
import { getInitialMarket } from './UnifiedEventsListMarkets';
import { isNotOutrightsEventItem, shouldShowUnderOutrightsFilter } from 'src/domains/sportsbook/utils/isOutrightEvent';

export class UnifiedSportEventsListState {
    private readonly eventsCollection: EventsCollectionState;
    private readonly models: ModelsState;

    private readonly selectTab: UnifiedEventsListSelectTabState;

    public constructor(
        private readonly common: Common,
        private readonly sportId: string,
        private readonly competitionId: CompetitionId | null,
        selectTab: UnifiedEventsListSelectTabState
    ) {
        makeObservable(this);
        this.eventsCollection = EventsCollectionState.get(common);
        this.selectTab = selectTab;
        this.models = common.models;
    }

    @computed public get sport(): string {
        return this.sportId;
    }

    /**
     * Matches / In-play (and competition pages) use the remembered market chip.
     * Featured on sport pages always falls back to the sport default so a Matches
     * pick (e.g. totals) does not leak into Featured rows.
     */
    @computed public get shouldUseSelectedMarketFilter(): boolean {
        return this.isMarketFilterActive === true || this.competitionId !== null;
    }

    @computed public get filter_market(): MarketType | null {
        if (this.shouldUseSelectedMarketFilter === true) {
            return this.selectTab.eventsListMarkets.filters_market ?? null;
        }

        return getInitialMarket(this.sportId);
    }

    @computed public get filter_marketTemplateId(): string | null {
        return this.selectTab.eventsListMarkets.getMarketTemplateId(this.filter_market);
    }

    @computed public get filter_marketLine(): number | null {
        return this.selectTab.eventsListMarkets.getMarketLine(this.filter_market);
    }

    @computed.struct public get lastSelections(): Array<string> {
        return this.selectTab.eventsListMarkets.getLastSelectionsFor(this.filter_market);
    }

    @computed.struct public get lastSelectionsHeader(): Array<string> {
        return this.translateSelectionHeaders(this.lastSelections);
    }

    public translateSelectionHeaders = (headers: Array<string>): Array<string> => {
        return this.selectTab.eventsListMarkets.translateSelectionHeaders(headers);
    };

    public isOutrightsMarket = (): boolean => {
        if (this.selectTab.filter_outrights === true) {
            return true;
        }

        const coupon = this.selectTab.filter_coupon;
        return coupon === 'custom-outrights-market' || coupon === 'specials';
    };

    @computed public get isMarketFilterActive(): boolean {
        return (
            this.selectTab.filter_outrights === false &&
            (this.selectTab.filter_time !== null || this.selectTab.filter_inPlay === true)
        );
    }

    @computed public get shouldFilterCollectionByMarket(): boolean {
        if (this.isOutrightsMarket()) {
            return false;
        }

        if (this.filter_marketTemplateId === null) {
            return false;
        }
        if (this.competitionId !== null) {
            return true;
        }
        return this.isMarketFilterActive;
    }

    public get collection(): EventsCollectionList {
        if (this.isOutrightsMarket() || this.competitionId !== null) {
            return this.eventsCollection.listOfSport(this.sportId);
        }

        if (this.shouldFilterCollectionByMarket === false || this.filter_marketTemplateId === null) {
            return this.eventsCollection.listOfSport(this.sportId);
        }

        return this.eventsCollection.listOfSport(this.sportId, {
            templateId: this.filter_marketTemplateId,
            line: this.filter_marketLine,
        });
    }

    @computed public get sportWideCollectionQuery(): EventsCollectionQueryModel | null {
        return this.eventsCollection.listOfSport(this.sportId).getQuery() ?? null;
    }

    @computed public get scopedCollectionQueryForFilters(): EventsCollectionQueryModel | null {
        const events = this.sportWideCollectionQuery;

        if (events === null) {
            return null;
        }

        if (this.competitionId === null) {
            return events;
        }

        return events.filterByCompetition((competitionId) => competitionId === this.competitionId?.toOldId());
    }

    @computed public get outrightsCompetitionsCount(): number {
        if (this.outrightEvents === null) {
            return 0;
        }

        return this.outrightEvents.competitionIds.length;
    }

    @computed public get outrightsCount(): number {
        if (this.outrightEvents === null) {
            return 0;
        }

        return this.outrightEvents.length;
    }

    @computed public get inPlayCount(): number {
        if (this.inPlayEvents === null) {
            return 0;
        }

        return this.inPlayEvents.length;
    }

    @computed public get isLoading(): boolean {
        return this.collection.isLoading;
    }

    @computed public get collectionQuery(): EventsCollectionQueryModel | null {
        return this.collection.getQuery() ?? null;
    }

    @computed.struct public get competitionsArray(): Array<EventsCollectionQueryCompetitionType> {
        return this.collectionQuery?.competitionsSortById ?? [];
    }

    @computed.struct public get outrightsForListView(): Array<
        EventsCollectionQueryCompetitionType & {
            flagCode: string;
            startTime: number | undefined;
            id2: EventId | null | undefined;
        }
    > {
        const eventsQuery = this.outrightEvents;
        if (eventsQuery === null) {
            return [];
        }
        const competitions: Array<{
            id: number;
            id2: EventId | null | undefined;
            name: string;
            displayOrder: number;
            flagCode: string;
            startTime: number | undefined;
        }> = [];

        for (const sport of eventsQuery.value.sports) {
            for (const competition of sport.competitions) {
                if (competition.events.length > 0) {
                    const firstEvent = competition.events[0];
                    const name =
                        competition.events.length === 1 ? firstEvent?.name ?? competition.name : competition.name;

                    competitions.push({
                        id: competition.id,
                        id2: firstEvent?.id2,
                        name,
                        displayOrder: competition.displayOrder,
                        startTime: firstEvent?.startTime,
                        flagCode: resolveCompetitionFlagCode(competition.name, firstEvent?.tags['country'] ?? null),
                    });
                }
            }
        }

        competitions.sort((a, b) => (a.startTime ?? 0) - (b.startTime ?? 0));

        return competitions;
    }

    @computed.struct public get competitionsForListView(): Array<
        EventsCollectionQueryCompetitionType & { flagCode: string }
    > {
        const events = this.events;

        if (events === null) {
            return [];
        }

        const competitions: Array<{ id: number; name: string; displayOrder: number; flagCode: string }> = [];

        for (const sport of events.value.sports) {
            for (const competition of sport.competitions) {
                if (competition.events.length > 0) {
                    competitions.push({
                        id: competition.id,
                        name: competition.name,
                        displayOrder: competition.displayOrder,
                        flagCode: resolveCompetitionFlagCode(
                            competition.name,
                            competition.events[0]?.tags['country'] ?? null
                        ),
                    });
                }
            }
        }

        competitions.sort((a, b) => b.displayOrder - a.displayOrder);

        return competitions;
    }

    @computed public get flagCodeByCompetitionId() {
        const competition = this.eventsForView?.value.sports[0]?.competitions.find(
            (competition) => competition.id === this.competitionId?.toOldId()
        );

        return resolveCompetitionFlagCode(competition?.name, competition?.events[0]?.tags['country'] ?? null);
    }

    public filterByCompetition(routeId: CompetitionId): EventsCollectionQueryModel | null {
        return this.visibleEvents?.filterByCompetition((competitionId) => competitionId === routeId.toOldId()) ?? null;
    }

    @computed public get marketForEnv(): Array<ItemType> {
        return getMarkets(LanguagesState.get(this.common).getTranslation, this.sportId);
    }

    @action public changeFilter_market = (id: MarketType | null): void => {
        this.selectTab.eventsListMarkets.filters_market = id;
    };

    @action public setFilterInPlay = (value: boolean): void => {
        this.selectTab.filter_inPlay = value;

        if (value === true) {
            this.selectTab.filter_outrights = false;
            this.selectTab.filter_time = null;

            if (this.filter_market === null && this.marketForEnv.length > 0) {
                this.changeFilter_market(this.marketForEnv[0]?.id ?? null);
            }
        }
    };

    @action public setFilterOutrights = (value: boolean): void => {
        this.selectTab.filter_outrights = value;

        if (value === true) {
            this.selectTab.filter_inPlay = false;
            // This row uncommented cause missing odds when moving from Outrights to any other filter
            //this.changeFilter_market(null);
            this.selectTab.filter_time = null;
        }
    };

    @action public setFilterTime = (value: SportsTimeFilterType | null): void => {
        this.selectTab.filter_time = value;

        if (value !== null) {
            this.selectTab.filter_inPlay = false;
            this.selectTab.filter_outrights = false;

            if (this.filter_market === null && this.marketForEnv.length > 0) {
                this.changeFilter_market(this.marketForEnv[0]?.id ?? null);
            }
        }
    };

    @action public clearSecondaryFilters = (): void => {
        this.selectTab.filter_inPlay = false;
        this.selectTab.filter_outrights = false;
        this.selectTab.filter_time = null;
    };

    private matchLocalTimeRange(startTimeMs: number, from: DateTime, to: DateTime): boolean {
        return from.unixMs() <= startTimeMs && startTimeMs <= to.unixMs();
    }

    private applyTimeFilter(
        events: EventsCollectionQueryModel,
        timeFilter: SportsTimeFilterType
    ): EventsCollectionQueryModel {
        if (timeFilter === 'all') {
            return events;
        }

        const now = DateTime.current();

        return events.filter((event) => {
            const { startTime } = event;

            if (timeFilter === 'today') {
                return this.matchLocalTimeRange(startTime, now.startOfDays(), now.endOfDays());
            }

            if (timeFilter === 'tomorrow') {
                const tomorrow = now.addDays(1);
                return this.matchLocalTimeRange(startTime, tomorrow.startOfDays(), tomorrow.endOfDays());
            }

            if (timeFilter === 'current-week') {
                return this.matchLocalTimeRange(startTime, now.startOfIsoWeek(), now.endOfIsoWeek());
            }

            const nextWeek = now.addWeeks(1);
            return this.matchLocalTimeRange(startTime, nextWeek.startOfIsoWeek(), nextWeek.endOfIsoWeek());
        });
    }

    private filterOutrightEvents(events: EventsCollectionQueryModel): EventsCollectionQueryModel {
        return events.filter(shouldShowUnderOutrightsFilter);
    }

    private filterIsNotOutrightEvents(events: EventsCollectionQueryModel): EventsCollectionQueryModel {
        return events.filter(isNotOutrightsEventItem);
    }

    private filterInPlayEvents(events: EventsCollectionQueryModel): EventsCollectionQueryModel {
        return events.filter(
            (event) =>
                this.common.serverTime.inPlay.matchMs(event.startTime) &&
                event.state === 'open' &&
                event.timeline !== 'finished'
        );
    }

    private filterEventsWithoutSelectedMarket(events: EventsCollectionQueryModel): EventsCollectionQueryModel {
        if (this.shouldFilterCollectionByMarket === false || this.filter_marketTemplateId === null) {
            return events;
        }

        return events.filter((event) => {
            const eventModel = this.models.getEvent(event.id);

            if (eventModel === null) {
                return true;
            }

            const simpleMarkets = eventModel.eventMarkets;

            if (simpleMarkets === null) {
                return true;
            }

            return simpleMarkets.some((item) => {
                const filterTemplateId = this.filter_marketTemplateId;
                if (matchesMarketFilterTemplate(filterTemplateId, item.templateId) === false) {
                    return false;
                }

                if (this.filter_marketLine === null) {
                    return true;
                }

                return item.keyLine === this.filter_marketLine;
            });
        });
    }

    public get visibleEvents(): EventsCollectionQueryModel | null {
        return this.collectionQuery;
    }

    @computed public get scottishTemplateId(): Array<number> {
        const competitionsArray = this.competitionsArray;
        const idsArray: Array<number> = [];

        for (const competition of competitionsArray) {
            for (const rx of scotlandRegex) {
                if (rx.test(competition.name.toLowerCase())) {
                    idsArray.push(competition.id);
                }
            }
        }

        return idsArray;
    }

    @computed public get welshTemplateId(): Array<number> {
        const competitionsArray = this.competitionsArray;
        return competitionsArray
            .filter(({ name }) => walesTemplateId.some((templateId) => templateId === name))
            .map(({ id }) => id);
    }

    @computed public get europeanEliteTemplateId(): Array<number> {
        const competitionsArray = this.competitionsArray;
        const idsArray: Array<number> = [];

        for (const competition of competitionsArray) {
            if (europeanEliteTemplate.includes(competition.name)) {
                idsArray.push(competition.id);
            }
        }

        return idsArray;
    }

    @computed private get eventsForView(): EventsCollectionQueryModel | null {
        if (this.visibleEvents === null) {
            return null;
        }
        return this.visibleEvents.filter((event) => {
            const date = DateTime.from(event.startTime);
            if (date === null) {
                return false;
            }
            return DateTime.current().subtractDays(7).isBefore(date);
        });
    }

    @computed public get getFilteredEvent(): EventsCollectionQueryModel | null {
        let events = this.eventsForView;
        const isOutrightsFilterActive = this.selectTab.filter_outrights === true;

        if (this.competitionId !== null) {
            events = this.filterByCompetition(this.competitionId);
        }

        if (this.selectTab.filter_inPlay === true && events !== null) {
            events = this.filterInPlayEvents(events);
        }

        if (isOutrightsFilterActive && events !== null) {
            events = this.filterOutrightEvents(events);
        }

        if (!isOutrightsFilterActive && events !== null) {
            const notOutrightEvents = this.filterIsNotOutrightEvents(events);

            if (notOutrightEvents.events.length > 0) {
                events = notOutrightEvents;
            }
        }

        if (this.selectTab.filter_time !== null && events !== null) {
            events = this.applyTimeFilter(events, this.selectTab.filter_time);
        }

        if (events !== null) {
            events = this.filterEventsWithoutSelectedMarket(events);
        }

        return events;
    }

    @computed public get events(): EventsCollectionQueryModel | null {
        return this.getFilteredEvent;
    }

    @computed public get inPlayEvents(): EventsCollectionQueryModel | null {
        const events = this.scopedCollectionQueryForFilters;
        return events !== null ? this.filterInPlayEvents(this.filterIsNotOutrightEvents(events)) : null;
    }

    @computed public get outrightEvents(): EventsCollectionQueryModel | null {
        const events = this.scopedCollectionQueryForFilters;
        return events !== null ? this.filterOutrightEvents(events) : null;
    }

    @computed public get showEvents(): boolean {
        const filteredEvents = this.getFilteredEvent;
        if (filteredEvents === null) {
            return false;
        }
        return filteredEvents.length > 0;
    }

    @computed public get filter_inPlay(): boolean {
        return this.selectTab.filter_inPlay;
    }

    @computed public get filter_outrights(): boolean {
        return this.selectTab.filter_outrights;
    }

    @computed public get filter_time(): SportsTimeFilterType | null {
        return this.selectTab.filter_time;
    }
}
